import { formatAmount, formatShareBps, USD } from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@loot-os/ui/components/tooltip";
import { formatNumber } from "@loot-os/ui/lib/format";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useState } from "react";

import { DetailField, DetailGroup } from "../components/DetailFields";
import { ErrorAlert } from "../components/ErrorAlert";
import { ExpandableRow } from "../components/ExpandableRow";
import { ShopifyOrderLink } from "../components/ShopifyLinks";
import { TableMessageRow } from "../components/TableMessageRow";
import { formatDate, formatPreciseDateTime } from "../lib/format";
import {
  USAGE_CHARGE_STATUS_TEXT,
  type UsageChargeRow,
  type UsageChargeStatus,
  type UsageChargeTotal,
  usageChargeTotals,
} from "../lib/shopifyFees";
import { useTRPC } from "../lib/trpc";
import { useShopifyShop } from "../lib/useShopifyShop";

/** Every fee is USD, which every linked shop sells and is billed in, not the display currency. */
const usd = (cents: number) => formatAmount(cents, USD);

const STATUS_BADGE: Record<UsageChargeStatus, "secondary" | "outline" | "destructive"> = {
  applied: "secondary",
  queued: "outline",
  failed: "destructive",
  skipped: "outline",
};

const COLUMNS = 7;

/**
 * Shopify → Fees: Loot Labs' fee on every settled box unit (`shopify_usage_charges`), the totals
 * by status over all of them, then each charge, newest first; a row opens in place to the rest of
 * what was recorded. Billed means Shopify accepted the usage record, as Loot OS recorded it;
 * nothing here is read from Shopify.
 */
export function ShopifyFeesPage() {
  const trpc = useTRPC();
  const { tenantId } = useParams({ strict: false }) as { tenantId: string };
  const { shopHandle } = useShopifyShop();
  const [expanded, setExpanded] = useState<string | null>(null);
  const usageCharges = useQuery(trpc.shopify.usageCharges.queryOptions());
  const totals = usageChargeTotals(usageCharges.data?.totals);
  const charges = usageCharges.data?.charges ?? [];
  const truncated = totals.reduce((sum, t) => sum + t.count, 0) > charges.length;

  if (usageCharges.error) return <ErrorAlert message={usageCharges.error.message} />;

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Fees</CardTitle>
          <CardDescription>
            Loot Labs' fee on every settled box, billed to the merchant as a Shopify usage charge.
            All time, USD.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-x-6 gap-y-4 xl:grid-cols-4">
            {totals.map((total) => (
              <TotalFigure key={total.status} total={total} />
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="min-w-0">
        <CardHeader>
          <CardTitle>Charges</CardTitle>
          <CardDescription>
            One row per settled box, newest first
            {truncated && usageCharges.data
              ? `, the latest ${formatNumber(usageCharges.data.limit)}`
              : ""}
            . A queued charge is retried every five minutes; after ten refusals it is marked failed
            and waits.
          </CardDescription>
        </CardHeader>
        {/* A container, so an open row's detail can take the card's width and not the table's. */}
        <CardContent className="@container flex flex-col gap-2">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>Time</TableHead>
                <TableHead>Order</TableHead>
                <TableHead className="text-right">Paid</TableHead>
                <TableHead className="text-right">Rate</TableHead>
                <TableHead className="text-right">Fee</TableHead>
                <TableHead>State</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {usageCharges.isPending && (
                <TableMessageRow colSpan={COLUMNS}>Loading…</TableMessageRow>
              )}
              {usageCharges.isSuccess && charges.length === 0 && (
                <TableMessageRow colSpan={COLUMNS}>No box has settled yet.</TableMessageRow>
              )}
              {charges.map((c) => (
                <ExpandableRow
                  key={c.id}
                  detailId={`shopify-fee-${c.id}`}
                  label={`Show the fee on ${c.orderName}`}
                  isOpen={expanded === c.id}
                  onToggle={() => setExpanded(expanded === c.id ? null : c.id)}
                  colSpan={COLUMNS}
                  detail={<ChargeDetail charge={c} tenantId={tenantId} shopHandle={shopHandle} />}
                >
                  <TableCell className="text-muted-foreground">{formatDate(c.createdAt)}</TableCell>
                  <TableCell>
                    <ShopifyOrderLink shopHandle={shopHandle} orderGid={c.orderGid}>
                      {c.orderName}
                    </ShopifyOrderLink>
                    {c.unitIndex > 0 && (
                      <span className="text-muted-foreground"> · {c.unitIndex + 1}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{usd(c.volumeCents)}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {c.status === "skipped" ? "—" : formatShareBps(c.rateBps)}
                  </TableCell>
                  <TableCell className="text-right font-medium tabular-nums">
                    {usd(c.amountCents)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={STATUS_BADGE[c.status]}>
                      {USAGE_CHARGE_STATUS_TEXT[c.status].label}
                    </Badge>
                  </TableCell>
                </ExpandableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

/** An open row's detail: the charge's billing record, then the sale it was taken on. */
function ChargeDetail({
  charge: c,
  tenantId,
  shopHandle,
}: {
  charge: UsageChargeRow;
  tenantId: string;
  shopHandle: string | null;
}) {
  return (
    // Its own container: the columns follow the width the panel is given, not the window's.
    <div className="@container">
      <div className="grid gap-x-12 gap-y-6 py-3 @3xl:grid-cols-2">
        <DetailGroup title="Charge">
          <DetailField label="State">{USAGE_CHARGE_STATUS_TEXT[c.status].hint}</DetailField>
          <DetailField label={c.status === "skipped" ? "Reason" : "Last error"}>
            {c.lastError}
          </DetailField>
          <DetailField label="Attempts">{c.attempts}</DetailField>
          <DetailField label="Settled">{formatPreciseDateTime(c.createdAt)}</DetailField>
          <DetailField label="Billed">
            {c.appliedAt ? formatPreciseDateTime(c.appliedAt) : null}
          </DetailField>
          <DetailField label="Usage record" mono copy={c.usageRecordGid}>
            {c.usageRecordGid}
          </DetailField>
          <DetailField label="Charge id" mono copy={c.id}>
            {c.id}
          </DetailField>
        </DetailGroup>

        <DetailGroup title="Sale">
          <DetailField label="Order" copy={c.orderName}>
            <ShopifyOrderLink
              shopHandle={shopHandle}
              orderGid={c.orderGid}
              className="text-primary hover:underline"
            >
              {c.orderName}
            </ShopifyOrderLink>
          </DetailField>
          <DetailField label="Unit">{c.unitIndex + 1}</DetailField>
          <DetailField label="Box">
            <Link
              to="/tenants/$tenantId/boxes/$boxId"
              params={{ tenantId, boxId: c.boxId }}
              className="text-primary hover:underline"
            >
              {c.boxName}
            </Link>
          </DetailField>
          <DetailField label="Purchase id" mono copy={c.purchaseId}>
            {c.purchaseId}
          </DetailField>
          <DetailField label="Order id" mono copy={c.orderGid}>
            {c.orderGid}
          </DetailField>
        </DetailGroup>
      </div>
    </div>
  );
}

/** One status's total: the fee, or for unbilled boxes the sales that billed nothing. */
function TotalFigure({ total }: { total: UsageChargeTotal }) {
  const { label, hint } = USAGE_CHARGE_STATUS_TEXT[total.status];
  const boxes = `${formatNumber(total.count)} ${total.count === 1 ? "box" : "boxes"}`;
  const skipped = total.status === "skipped";
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <Tooltip>
        <TooltipTrigger
          render={
            <span className="w-fit cursor-help text-xs text-muted-foreground underline decoration-muted-foreground/50 decoration-dotted underline-offset-4" />
          }
        >
          {label}
        </TooltipTrigger>
        <TooltipContent>{hint}</TooltipContent>
      </Tooltip>
      <span
        className={
          total.status === "failed" && total.count > 0
            ? "text-2xl font-semibold text-destructive tabular-nums"
            : "text-2xl font-semibold tabular-nums"
        }
      >
        {skipped ? boxes : usd(total.amountCents)}
      </span>
      <span className="text-xs text-muted-foreground">
        {skipped
          ? `${usd(total.volumeCents)} of sales`
          : `${boxes}, ${usd(total.volumeCents)} of sales`}
      </span>
    </div>
  );
}
